import { minutesToMilliseconds, subMinutes, addMinutes } from 'date-fns';

import type { IService, ITimeslot } from '@types';
import type { Cell } from './Cell'
import type { Calendar } from './useCalendar'
import { getServiceBreak } from '../priceLibrary/service';

// Проверка слотов на активность и удаление промежутков недостаточных для минимальной брони
export function getDisabledSlots(cells: Cell[], calendar: Calendar, minLength = 0) {
	const minLengthInMilliseconds = minutesToMilliseconds(minLength);
	const res: Disabled[] = []
	let currentBubble: Cell | undefined = undefined

	cells.forEach((cell) => {
		if (calendar?.isCellDisabled(cell)) {
			if (currentBubble) {
				currentBubble.to = cell.from;
				if (((+currentBubble.to || 0) - (+currentBubble.from || 0)) < minLengthInMilliseconds) {
					res.push(new Disabled(currentBubble))
					currentBubble = undefined
				}
			}
		} else if (!currentBubble) {
			currentBubble = { from: cell.from } as Cell;
		}
	})
	return res;
}

export interface SlotAvailability {
	total: number;
	occupied: number;
	free: number;
}

export interface IDisabledParams extends Partial<Cell> {
	availability?: SlotAvailability
}

function getType(from?: Date, to?: Date): 'from' | 'to' | 'between' | 'empty' {
	if (from && to) return 'between'
	if (from) return 'from'
	if (to) return 'to'
	return 'empty'
}

function genAvailability(value: SlotAvailability): SlotAvailability {
	let { occupied, free, total = 0 } = value || {}

	if (total < 0) total = 0

	if (free === undefined) free = total - (occupied || 0)
	if (free < 0) free = 0
	if (free > total) free = total

	if (occupied === undefined) occupied = total - (free || 0)
	if (occupied < 0) occupied = 0
	if (occupied > total) occupied = total

	if (free > total - occupied) free = total - occupied

	return { total, occupied, free }
}

export class Disabled {
	availability
	from
	to
	type
	constructor({ availability, from, to }: IDisabledParams = {}) {
		this.availability = genAvailability(availability)
		this.from = from
		this.to = to
		this.type = getType(from, to)
	}

	private check(a: Date, b: Date, operator: '>' | '<', isInclude = true) {
		switch (operator) {
			case '>': {
				if (isInclude) return a >= b
				return a > b
			}
			case '<': {
				if (isInclude) return a <= b
				return a < b
			}
			default: return false
		}
	}

	private isBefore(cell: Cell | Date) {
		if (this.type !== 'to' || !this.to) return false
		const _check = (date: Date | string) => this.check(new Date(date), this.to, '<')
		if (cell instanceof Date) {
			return _check(cell)
		} else {
			return _check(cell.from) || _check(cell.to)
		}
	}

	private isAfter(cellOrDate: Cell | Date) {
		if (this.type !== 'from' || !this.from) return false
		const _check = (date: Date | string) => this.check(new Date(date), this.from, '>')
		if (cellOrDate instanceof Date) {
			return _check(cellOrDate)
		} else {
			return _check(cellOrDate.from) || _check(cellOrDate.to)
		}
	}

	private isBetween(cellOrDate: Cell | Date) {
		if (!this.from || !this.to) return false
		const _isBetween = ({
			date,
			checker = {
				from: this.from!,
				to: this.to!,
			},
			includeCheck = true,
		}: {
			date: Date | string
			checker?: Cell
			includeCheck?: boolean
		}) => {
			date = new Date(date)
			return (
				this.check(date, checker.from, '>', includeCheck) &&
				this.check(date, checker.to, '<', includeCheck)
			)
		}

		if (cellOrDate instanceof Date) {
			return _isBetween({
				date: cellOrDate,
			})
		} else {
			const include = // Слоты входят в заблокированное пространство
				_isBetween({
					date: cellOrDate.from,
				}) &&
				_isBetween({
					date: cellOrDate.to,
				})
			const disableInclude = // Заблокированное пространство входит в слоты
				_isBetween({
					date: this.from,
					checker: cellOrDate,
					includeCheck: false,
				}) ||
				_isBetween({
					date: this.to,
					checker: cellOrDate,
					includeCheck: false,
				})
			return include || disableInclude
		}
	}

	isDisabled(cellOrDate: Cell | Date, checkAvailability = true,): boolean {
		if (checkAvailability && this.availability?.free > 0) return false

		switch (this.type) {
			case 'between': return this.isBetween(cellOrDate)
			case 'to': return this.isBefore(cellOrDate)
			case 'from': return this.isAfter(cellOrDate)
			default: return false
		}
	}
}


export function useDisabledList<T extends (Cell | Disabled)[] = Cell[]>(items: T) {
	const list = items.map((item) => item instanceof Disabled ? item : new Disabled(item));

	function findInDisabledList(cellOrDate: Cell | Date, checkAvailability?: boolean) {
		return list.find(item => item.isDisabled(cellOrDate, checkAvailability));
	}

	return {
		findInDisabledList,
	};
}

export function getDisabledFromOrderedSlots(slots: ITimeslot[], service: IService): Disabled[] {
	const mainOffset = getServiceBreak(service);
	return slots.map(({ start, end, availableSpots }) => new Disabled({
		availability: {
			total: availableSpots,
		},
		from: subMinutes(start, mainOffset),
		to: addMinutes(end, mainOffset),
	}));
}